import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersAPI, invoicesAPI, materialsAPI } from '../services/api';

const ManualInvoicePage = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [error, setError] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    title: 'Kleinarbeiten',
    hourly_rate: 120,
    labor_hours: 0,
    payment_type: 'invoice',
    notes: '',
    no_tax: false,
    travel_allowance: 0,
    items: []
  });
  
  const [newItem, setNewItem] = useState({
    description: '',
    quantity: 1,
    unit: 'Stück',
    price_per_unit: 0,
    material_id: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const customersResponse = await customersAPI.getAll();
        setCustomers(customersResponse.data.customers);
        
        const materialsResponse = await materialsAPI.getAll();
        setMaterials(materialsResponse.data.materials);
        
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        setError('Die Daten konnten nicht geladen werden: ' + 
                (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Wenn Kunden ausgewählt wird, setze den selectedCustomer
    if (name === 'customer_id' && value) {
      const customer = customers.find(c => c.id.toString() === value);
      setSelectedCustomer(customer);
    }
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    
    // Wenn ein gespeichertes Material ausgewählt wurde
    if (name === 'material_id' && value) {
      const selectedMaterial = materials.find(m => m.id.toString() === value);
      if (selectedMaterial) {
        setNewItem({
          description: selectedMaterial.name,
          quantity: 1,
          unit: selectedMaterial.unit,
          price_per_unit: selectedMaterial.price_per_unit,
          material_id: value
        });
        return;
      }
    }
    
    setNewItem(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addItem = () => {
    if (!newItem.description || parseFloat(newItem.quantity) <= 0) {
      alert('Bitte geben Sie eine Beschreibung und eine gültige Menge ein.');
      return;
    }
    
    const itemWithTotal = {
      ...newItem,
      total: parseFloat(newItem.quantity) * parseFloat(newItem.price_per_unit)
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, itemWithTotal]
    }));
    
    // Zurücksetzen für neuen Eintrag
    setNewItem({
      description: '',
      quantity: 1,
      unit: 'Stück',
      price_per_unit: 0,
      material_id: '' // Material-ID zurücksetzen
    });
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    // Materialkosten aus den Items berechnen
    const materialCost = formData.items.reduce((sum, item) => {
      return sum + parseFloat(item.quantity) * parseFloat(item.price_per_unit);
    }, 0);
    
    // Arbeitskosten berechnen
    const laborCost = parseFloat(formData.labor_hours) * parseFloat(formData.hourly_rate);
    
    // Anfahrtspauschale
    const travelAllowance = parseFloat(formData.travel_allowance) || 0;
    
    // Zwischensumme berechnen
    const subtotal = materialCost + laborCost + travelAllowance;
    
    // MwSt. berechnen
    const taxRate = formData.no_tax ? 0 : 0.077; // 7.7% MwSt. oder 0% wenn no_tax aktiv
    const tax = subtotal * taxRate;
    
    // Gesamtbetrag
    const total = subtotal + tax;
    
    return {
      materialCost,
      laborCost,
      travelAllowance,
      subtotal,
      tax,
      total
    };
  };

  const formatMySQLDate = (date) => {
    return date.toISOString().slice(0, 19).replace('T', ' ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      alert('Bitte wählen Sie einen Kunden aus.');
      return;
    }
    
    try {
      const calculation = calculateTotal();
      
      // Rechnungsdaten vorbereiten
      const currentDate = new Date();
      const dueDate = new Date(currentDate);
      dueDate.setDate(dueDate.getDate() + 20); // 20 Tage Zahlungsfrist
      
      // Werte für die Datenbank vorbereiten
      const invoiceData = {
        is_manual: true,
        customer_id: parseInt(formData.customer_id, 10),
        invoice_date: formatMySQLDate(currentDate),
        due_date: formatMySQLDate(dueDate),
        title: formData.title,
        hourly_rate: parseFloat(formData.hourly_rate) || 0,
        payment_type: formData.payment_type,
        notes: formData.notes || "",
        material_items: formData.items,
        material_cost: parseFloat(calculation.materialCost.toFixed(2)) || 0,
        labor_hours: parseFloat(formData.labor_hours) || 0,
        labor_cost: parseFloat(calculation.laborCost.toFixed(2)) || 0,
        travel_allowance: parseFloat(calculation.travelAllowance.toFixed(2)) || 0,
        subtotal: parseFloat(calculation.subtotal.toFixed(2)) || 0,
        tax: parseFloat(calculation.tax.toFixed(2)) || 0,
        total: parseFloat(calculation.total.toFixed(2)) || 0,
        status: formData.payment_type === 'cash' ? 'paid' : 'pending',
        no_tax: formData.no_tax
      };
      
      console.log('Erstelle manuelle Rechnung mit Daten:', invoiceData);
      
      // Rechnung erstellen
      const response = await invoicesAPI.createManual(invoiceData);
      console.log('Rechnung erstellt:', response.data);
      
      alert(`Rechnung erfolgreich erstellt!`);
      navigate(`/rechnungen/${response.data.invoice.id}`);
    } catch (err) {
      console.error('Fehler beim Erstellen der Rechnung:', err);
      console.error('Response Status:', err.response?.status);
      console.error('Response Data:', err.response?.data);
      console.error('Error Message:', err.message);
      
      setError('Fehler beim Erstellen der Rechnung: ' + 
               (err.response?.data?.error || err.message));
    }
  };

  // Formatieren von Beträgen
  const formatAmount = (amount) => {
    return 'CHF ' + parseFloat(amount).toFixed(2);
  };

  if (loading) {
    return <div className="text-center py-10">Lade Kundendaten...</div>;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
        <button 
          onClick={() => navigate('/rechnungen')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
        >
          Zurück zur Rechnungsübersicht
        </button>
      </div>
    );
  }

  const calculation = calculateTotal();

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">Manuelle Rechnung erstellen</h1>
      
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-medium mb-4">Kundeninformationen</h2>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Kunde auswählen</label>
          <select
            name="customer_id"
            value={formData.customer_id}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">-- Kunde auswählen --</option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.company_name || `${customer.first_name} ${customer.last_name}`}
              </option>
            ))}
          </select>
        </div>
        
        {selectedCustomer && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-600">Kunde</p>
              <p className="font-medium">
                {selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`}
              </p>
              <p>{selectedCustomer.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Adresse</p>
              <p>{selectedCustomer.address}</p>
              <p>{selectedCustomer.postal_code} {selectedCustomer.city}</p>
            </div>
          </div>
        )}
      </div>
      
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-medium mb-4">Rechnungsposten</h2>
        
        {/* Formular für einen neuen Posten */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium mb-3">Neuen Posten hinzufügen</h3>
          
          {/* Material aus der Datenbank auswählen */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Gespeichertes Material auswählen (optional)</label>
            <select
              name="material_id"
              value={newItem.material_id}
              onChange={handleItemChange}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">-- Neuen Posten manuell eingeben --</option>
              {materials.map(material => (
                <option key={material.id} value={material.id}>
                  {material.name} ({material.price_per_unit} CHF / {material.unit})
                </option>
              ))}
            </select>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 mb-2">Beschreibung</label>
              <input
                type="text"
                name="description"
                value={newItem.description}
                onChange={handleItemChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Einheit</label>
              <input
                type="text"
                name="unit"
                value={newItem.unit}
                onChange={handleItemChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Menge</label>
              <input
                type="number"
                name="quantity"
                value={newItem.quantity}
                onChange={handleItemChange}
                min="0.01"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Preis pro Einheit (CHF)</label>
              <input
                type="number"
                name="price_per_unit"
                value={newItem.price_per_unit}
                onChange={handleItemChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={addItem}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
          >
            Posten hinzufügen
          </button>
        </div>
        
        {/* Liste der hinzugefügten Posten */}
        {formData.items.length > 0 ? (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Hinzugefügte Posten</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">Beschreibung</th>
                    <th className="px-4 py-2 text-left">Menge</th>
                    <th className="px-4 py-2 text-left">Einheit</th>
                    <th className="px-4 py-2 text-left">Preis</th>
                    <th className="px-4 py-2 text-left">Summe</th>
                    <th className="px-4 py-2 text-left">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-2">{item.description}</td>
                      <td className="px-4 py-2">{item.quantity}</td>
                      <td className="px-4 py-2">{item.unit}</td>
                      <td className="px-4 py-2">CHF {parseFloat(item.price_per_unit).toFixed(2)}</td>
                      <td className="px-4 py-2">CHF {(item.quantity * item.price_per_unit).toFixed(2)}</td>
                      <td className="px-4 py-2">
                        <button 
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Entfernen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="mb-6 text-gray-500">Keine Posten hinzugefügt</p>
        )}
      </div>
      
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-medium mb-4">Rechnungsdetails</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Titel der Rechnung</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Stundenansatz (CHF)</label>
            <input
              type="number"
              name="hourly_rate"
              value={formData.hourly_rate}
              onChange={handleChange}
              min="1"
              step="1"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Arbeitsstunden</label>
            <input
              type="number"
              name="labor_hours"
              value={formData.labor_hours}
              onChange={handleChange}
              min="0"
              step="0.25"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Anfahrtspauschale (CHF)</label>
            <input
              type="number"
              name="travel_allowance"
              value={formData.travel_allowance}
              onChange={handleChange}
              min="0"
              step="0.05"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Zahlungsart</label>
            <div className="flex gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="payment_type"
                  value="cash"
                  checked={formData.payment_type === 'cash'}
                  onChange={handleChange}
                  className="form-radio h-4 w-4 text-primary-600"
                />
                <span className="ml-2">Bar bezahlt</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="payment_type"
                  value="invoice"
                  checked={formData.payment_type === 'invoice'}
                  onChange={handleChange}
                  className="form-radio h-4 w-4 text-primary-600"
                />
                <span className="ml-2">Rechnung (zahlbar innert 20 Tagen)</span>
              </label>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="no_tax"
                checked={formData.no_tax}
                onChange={handleChange}
                className="form-checkbox h-4 w-4 text-primary-600"
              />
              <span className="ml-2">Keine MwSt. berechnen</span>
            </label>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Bemerkungen (optional)</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            ></textarea>
          </div>
          
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h3 className="text-lg font-medium mb-4">Zusammenfassung</h3>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span>Material:</span>
                <span>{formatAmount(calculation.materialCost)}</span>
              </div>
              <div className="flex justify-between">
                <span>Arbeitsstunden:</span>
                <span>{parseFloat(formData.labor_hours).toFixed(2)} h</span>
              </div>
              <div className="flex justify-between">
                <span>Stundenansatz:</span>
                <span>CHF {parseFloat(formData.hourly_rate).toFixed(2)} / h</span>
              </div>
              <div className="flex justify-between">
                <span>Arbeitskosten:</span>
                <span>{formatAmount(calculation.laborCost)}</span>
              </div>
              {calculation.travelAllowance > 0 && (
                <div className="flex justify-between">
                  <span>Anfahrtspauschale:</span>
                  <span>{formatAmount(calculation.travelAllowance)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium">
                <span>Zwischensumme:</span>
                <span>{formatAmount(calculation.subtotal)}</span>
              </div>
              {!formData.no_tax && (
                <div className="flex justify-between">
                  <span>MwSt. (7.7%):</span>
                  <span>{formatAmount(calculation.tax)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold">
                <span>Gesamtbetrag:</span>
                <span>{formatAmount(calculation.total)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/rechnungen')}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded"
            >
              Rechnung erstellen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualInvoicePage;
